import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { KeyRound, ShieldAlert, CheckCircle2 } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | valid | invalid | done
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/reset-token/${token}`);
        if (data.valid) { setStatus("valid"); setEmail(data.email || ""); }
        else setStatus("invalid");
      } catch { setStatus("invalid"); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Mínimo 6 caracteres");
    if (pw !== pw2) return toast.error("As palavras-passe não coincidem");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setStatus("done");
      toast.success("Palavra-passe atualizada");
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao redefinir");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#0d0d0d] text-white">
      <div className="auk-ribbon h-2" />
      <div className="flex-1 flex flex-col justify-center px-6 py-8 max-w-md mx-auto w-full">
        <Link to="/login" className="text-xs text-white/60 mb-8 hover:text-white">← Voltar ao login</Link>

        {status === "checking" && (
          <div className="text-center text-white/60 text-sm">A validar link…</div>
        )}

        {status === "invalid" && (
          <div data-testid="reset-invalid" className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-red-900/40 border border-red-800 flex items-center justify-center mb-4 mx-auto">
              <ShieldAlert size={28} className="text-red-400" />
            </div>
            <h1 className="font-display text-2xl font-extrabold mb-2">Link inválido ou expirado</h1>
            <p className="text-sm text-white/70 leading-relaxed mb-6">
              Este link já foi utilizado, expirou, ou nunca foi válido.
              Pede um novo link de recuperação para continuar.
            </p>
            <Link to="/esqueci" data-testid="reset-request-new"
                  className="inline-block px-5 py-2.5 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] font-semibold text-sm transition-colors">
              Pedir novo link
            </Link>
          </div>
        )}

        {status === "valid" && (
          <>
            <div className="w-14 h-14 rounded-2xl bg-[#c8102e] flex items-center justify-center mb-4">
              <KeyRound size={26} />
            </div>
            <h1 className="font-display text-3xl font-extrabold mb-2">Nova palavra-passe</h1>
            {email && <p className="text-sm text-white/60 mb-6">Conta: <span className="text-white">{email}</span></p>}

            <form onSubmit={submit} className="space-y-3">
              <PasswordInput
                testid="reset-password"
                required
                minLength={6}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="Nova palavra-passe (mín. 6 caracteres)"
                autoComplete="new-password"
              />
              <PasswordInput
                testid="reset-password-confirm"
                required
                minLength={6}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                placeholder="Confirmar palavra-passe"
                autoComplete="new-password"
              />
              <button
                data-testid="reset-submit"
                type="submit" disabled={busy}
                className="w-full py-3 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] disabled:opacity-50 font-semibold transition-colors"
              >
                {busy ? "A guardar..." : "Redefinir palavra-passe"}
              </button>
            </form>
          </>
        )}

        {status === "done" && (
          <div data-testid="reset-done" className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-green-600 flex items-center justify-center mb-4 mx-auto">
              <CheckCircle2 size={28} />
            </div>
            <h1 className="font-display text-2xl font-extrabold mb-2">Tudo pronto</h1>
            <p className="text-sm text-white/70 leading-relaxed mb-6">
              A tua palavra-passe foi atualizada. Vais ser redirecionado(a) para o login…
            </p>
            <Link to="/login" className="text-[#ffd100] font-semibold text-sm hover:underline">
              Ir para o login agora
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
